import type { WorkCondition, WorkPageData } from "@/types/work.types";
import { vNextUrl } from "@/utility/url";

const SCHEMA_ORIGIN = "https://schema.org";
const PRODUCT_TYPE_BOOK = 1;
const PRICE_VALID_DAYS = 30;

const absoluteUrl = (value: string) => {
  if (!value) return undefined;

  try {
    return vNextUrl(value).toString();
  } catch {
    return undefined;
  }
};

const toDateOnly = (value?: string | null) => {
  if (!value) return undefined;

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? undefined
    : date.toISOString().slice(0, 10);
};

const toLanguageCode = (value?: string) => {
  const normalized = value?.trim().toLowerCase();
  const languageCodes: Record<string, string> = {
    english: "en",
    french: "fr",
    german: "de",
    italian: "it",
    japanese: "ja",
    portuguese: "pt",
    spanish: "es",
  };
  return normalized ? (languageCodes[normalized] ?? normalized) : undefined;
};

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getSelectedCondition = (data: WorkPageData) => {
  const format =
    data.formats.find((item) => item.idMedia === data.selectedMedia) ??
    data.formats[0];

  return (
    format?.conditions.find(
      (condition) => condition.idQuality === data.selectedQuality,
    ) ??
    format?.conditions.find(
      (condition) => condition.quantity > condition.inCartQuantity,
    )
  );
};

const getEditionUrl = (canonicalUrl: string, condition: WorkCondition) => {
  const url = new URL(canonicalUrl);
  if (condition.isbn.trim())
    url.searchParams.set("isbn", condition.isbn.trim());
  if (condition.idIq) url.searchParams.set("idiq", String(condition.idIq));
  if (!condition.isbn.trim() && condition.idAmazon) {
    url.searchParams.set("edition", String(condition.idAmazon));
  }
  return url.toString();
};

const getOfferConditions = (data: WorkPageData) => {
  const selectedCondition = getSelectedCondition(data);
  const conditions = data.formats
    .flatMap((format) => format.conditions)
    .filter(
      (condition) =>
        condition.price != null &&
        condition.idAmazon > 0 &&
        (condition.idIq != null || condition === selectedCondition),
    )
    .sort((left, right) => {
      const leftAvailable = left.quantity > left.inCartQuantity ? 1 : 0;
      const rightAvailable = right.quantity > right.inCartQuantity ? 1 : 0;
      return rightAvailable - leftAvailable;
    });

  const uniqueConditions = new Map<string, WorkCondition>();
  for (const condition of conditions) {
    const sku = condition.idIq ?? condition.idAmazon;
    const key = String(sku);
    if (!uniqueConditions.has(key)) uniqueConditions.set(key, condition);
  }

  if (selectedCondition?.price != null && selectedCondition.idAmazon > 0) {
    const selectedSku = String(
      selectedCondition.idIq ?? selectedCondition.idAmazon,
    );
    if (!uniqueConditions.has(selectedSku)) {
      uniqueConditions.set(selectedSku, selectedCondition);
    }
  }

  return [...uniqueConditions.values()];
};

export const buildWorkStructuredData = (
  data: WorkPageData,
  now = new Date(),
): Record<string, unknown> | null => {
  const { work } = data;
  if (!work) return null;

  const canonicalUrl = vNextUrl(
    `/w/${work.cleanUrl}/${work.idWork}/`,
  ).toString();
  const selectedCondition = getSelectedCondition(data);
  const edition = selectedCondition?.editionDetails;
  const isBook = work.idProductType === PRODUCT_TYPE_BOOK;
  const description =
    stripHtml(work.synopsis || work.marketingText) ||
    `${work.title} available from ThriftBooks.`;
  const image = absoluteUrl(
    selectedCondition?.imageUrlExtraLarge ||
      selectedCondition?.imageUrlLarge ||
      work.imageUrlLarge ||
      selectedCondition?.imageUrl ||
      work.imageUrl,
  );
  const publisherName = edition?.publisher.trim();
  const brandName = edition?.brandName.trim() || publisherName;
  const category = data.breadcrumbs.at(-1)?.name.trim();
  const isbn =
    selectedCondition?.ean.trim() ||
    selectedCondition?.isbn.trim() ||
    undefined;
  const priceValidUntil = new Date(now);
  priceValidUntil.setUTCDate(priceValidUntil.getUTCDate() + PRICE_VALID_DAYS);

  const offers = getOfferConditions(data).map((condition) => ({
    "@type": "Offer",
    url: getEditionUrl(canonicalUrl, condition),
    sku: String(condition.idIq ?? condition.idAmazon),
    gtin13: condition.ean.trim() || undefined,
    price: condition.price,
    priceCurrency: "USD",
    priceValidUntil: priceValidUntil.toISOString().slice(0, 10),
    availability:
      condition.quantity > condition.inCartQuantity
        ? `${SCHEMA_ORIGIN}/InStock`
        : `${SCHEMA_ORIGIN}/OutOfStock`,
    itemCondition:
      condition.idQuality === 1
        ? `${SCHEMA_ORIGIN}/NewCondition`
        : `${SCHEMA_ORIGIN}/UsedCondition`,
    seller: {
      "@type": "Organization",
      name: "ThriftBooks",
    },
  }));

  const authors = isBook
    ? data.authors.map((author) => ({
        "@type": "Person",
        name: author.name,
        url: absoluteUrl(author.url),
      }))
    : [];
  const reviews = data.reviews
    .filter((review) => review.rating > 0 && review.isVisible)
    .map((review) => ({
      "@type": "Review",
      datePublished: toDateOnly(review.postedAt),
      name: review.headline.trim() || undefined,
      reviewBody: review.body.trim() || undefined,
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.rating,
        bestRating: 5,
        worstRating: 1,
      },
      author: {
        "@type": "Person",
        name: review.customerName.trim() || "ThriftBooks User",
      },
      publisher: {
        "@type": "Organization",
        name: "ThriftBooks",
      },
    }));
  let author: (typeof authors)[number] | typeof authors | undefined;
  if (authors.length === 1) author = authors[0];
  if (authors.length > 1) author = authors;

  let schemaOffers: (typeof offers)[number] | typeof offers | undefined;
  if (offers.length === 1) schemaOffers = offers[0];
  if (offers.length > 1) schemaOffers = offers;

  return {
    "@context": SCHEMA_ORIGIN,
    "@type": isBook ? ["Product", "Book"] : "Product",
    "@id": canonicalUrl,
    url: canonicalUrl,
    name: work.title,
    image,
    description,
    author,
    isbn: isBook ? isbn : undefined,
    inLanguage: isBook ? toLanguageCode(edition?.language) : undefined,
    numberOfPages:
      isBook && edition && edition.numberOfPages > 0
        ? edition.numberOfPages
        : undefined,
    publisher:
      isBook && publisherName
        ? { "@type": "Organization", name: publisherName }
        : undefined,
    brand: brandName ? { "@type": "Brand", name: brandName } : undefined,
    category: category || undefined,
    datePublished: isBook ? toDateOnly(edition?.releaseDate) : undefined,
    aggregateRating:
      work.ratingCount > 0
        ? {
            "@type": "AggregateRating",
            ratingValue: work.rating,
            ratingCount: work.ratingCount,
            reviewCount: work.reviewCount,
            bestRating: 5,
            worstRating: 1,
          }
        : undefined,
    review: reviews.length > 0 ? reviews : undefined,
    offers: schemaOffers,
  };
};
